import get from 'lodash/get';
import moment from 'moment';
import {useEffect, useState} from 'react';

import AddIconMinimal from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import IconChevronDown from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/HighlightOff';
import Typography from '@mui/material/Typography';

import VacationTimeDialog from './VacationTimeDialog';
import {
  useGetVacationTimesQuery,
  useCreateVacationTimeMutation,
  useRemoveVacationTimeMutation
} from '../../redux/services/vacationTimeApi';
import config from "../../config/config";

const parseDaysString = vacationTime => (vacationTime.recurring_days || []).reduce((acc, next) => (
  acc ? `${acc}, ${next}` : next
), '');

const parseRecurringVacationTimeText = vacationTime => {
  let str = `Recurring every ${vacationTime.repeat_every_period_value}`;
  str += ` week${vacationTime.repeat_every_period_value === 1 ? '' : 's'}`;
  str += ` on ${parseDaysString(vacationTime)}`;
  if (vacationTime.recurring_start_time) {
    const today = moment().format('YYYY-MM-DD');
    console.log('vacationTime', vacationTime);
    const recurringStart = moment(`${today} ${vacationTime.recurring_start_time}`, 'YYYY-MM-DD h:mm a');
    const recurringEnd = moment(`${today} ${vacationTime.recurring_end_time}`, 'YYYY-MM-DD h:mm a');
    console.log('recurringStart', recurringStart);
    console.log('recurringEnd', recurringEnd);
    if (
      recurringStart.isBefore(moment().set('hour', 0).set('minute', 59))
      && recurringEnd.isAfter(moment().set('hour', 23).set('minute', 1))
    ) {
      str += ' all day';
    } else {
      str += ` at ${recurringStart.format('h:mm a')} - ${recurringEnd.format('h:mm a')}`;
    }
  }
  if (vacationTime.end_time) str += ` until ${moment(vacationTime.end_time).format('ll')}`;
  return str;
};

const VacationTimeItem = props => (
  <Stack direction='row' spacing={1} justifyContent='space-between'>
    <Stack justifyContent='center'>
      {props.vacationTime.is_recurring 
        ? 
          <Typography variant='body2' style={{color: 'rgba(0,0,0,0.55)'}}>
            {parseRecurringVacationTimeText(props.vacationTime)}
          </Typography>
        : <Typography variant='body2' style={{color: 'rgba(0,0,0,0.55)'}}>
          {
            props.vacationTime.start_time
            ? moment(props.vacationTime.start_time).format('ll h:mma')
            : 'No Limit'
          }
          {' '}-{' '}
          {
            props.vacationTime.end_time
            ? moment(props.vacationTime.end_time).format('ll h:mma')
            : 'No Limit'
          }
        </Typography>
      }
    </Stack>
    <IconButton
      color='primary'
      onClick={() => props.onDelete({id: props.vacationTime.id})}
    >
      <DeleteIcon/>
    </IconButton>
  </Stack>
);

const ListVacationTimes = props => {
  const [collapsed, setCollapsed] = useState(true);
  const sortedVacationTimes = (props.vacationTimes || [])
    .filter(vt => (new Date(vt.end_time) > new Date()))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  const recurringVacationTimes = (props.vacationtimes || []).filter(vt => vt.is_recurring);
  const allVacationTimes = [...recurringVacationTimes, ...sortedVacationTimes];
  return (
      <Box
          className='w-100'
          borderRadius={config().styles.defaultRadius}
          bgcolor={config().colors.white}
          border={config().styles.defaultBorder}
          boxShadow={config().styles.defaultBoxShadow}
          sx={{
            '&:hover': {
              opacity: 0.8,
            },
          }}
      >
      <Box style={{borderBottom: '1px solid rgba(0,0,0,0.1)'}}>
        <Stack 
          direction='row' 
          spacing={1} 
          padding={1} 
          justifyContent='space-between' 
          alignItems='center'
        >
          <Stack justifyContent='center'>
            <Typography variant='h4' style={{color: 'rgba(0,0,0,0.55)'}}>
              Away Time
            </Typography>
          </Stack>
          <Button
            variant='secondaryGradient'
            size={props.isMobile ? 'medium' : 'large'}
            color='secondary'
            onClick={props.onCreate}
          >
            New Away Time
          </Button>
        </Stack>
      </Box>
      <Stack spacing={0.25} padding={1}>
        {(allVacationTimes || [])
          .filter((t, i) => i < 3)
          .map(t =>
            <VacationTimeItem
              key={t.id}
              vacationTime={t}
              onDelete={props.onDelete}
            />
          )
        }
        {
          sortedVacationTimes && sortedVacationTimes.length > 3
          ? <>
            {collapsed
              ?
                <Stack direction='row' justifyContent='center'>
                  <Button onClick={() => setCollapsed(false)}>
                    View more <IconChevronDown />
                  </Button>
                </Stack>
              : sortedVacationTimes
                .filter((t, i) => i >= 3)
                .map(t =>
                  <VacationTimeItem vacationTime={t} onDelete={props.onDelete} />
                )
            }
          </>
          : null
        }
      </Stack>
    </Box>
  );
};

const handleDelete = ({
  vacationTimesQuery,
  removeVacationTime,
  setError
}) => async id => {
  const res = await removeVacationTime({id});
  if (res.error) return setError({message: 'Error deleting VacationTime'});
  setError(null);
  vacationTimesQuery.refetch();
};

const handleSubmit = ({
  createVacationTime,
  vacationTimesQuery,
  setVacationDialogIsOpen,
  setError,
}) => formData => {
  const response = createVacationTime(formData);
  if (response.error) return setError({message: 'Error creating vacation time'});
  setError(null);
  setVacationDialogIsOpen(false);
};

const VacationTimeEditor = props => {
  const [error, setError] = useState(null);
  const [vacationDialogIsOpen, setVacationDialogIsOpen] = useState(false);
  const vacationTimesQuery = useGetVacationTimesQuery();
  const [createVacationTime] = useCreateVacationTimeMutation();
  const [removeVacationTime] = useRemoveVacationTimeMutation();
  const vacationTimes = get(vacationTimesQuery, 'data', []);
  const onDelete = handleDelete({
    vacationTimesQuery,
    removeVacationTime,
    setError,
  });
  const onSubmit = handleSubmit({
    createVacationTime,
    vacationTimesQuery,
    setVacationDialogIsOpen,
    setError,
  });

  useEffect(() => {
    if (props.onEvent) {
      props.onEvent(vacationTimes && vacationTimes.length > 0);
    }
  }, [vacationTimes, props]);

  return (
    <Box width={vacationTimes && vacationTimes.length ? '100%' : 'auto'}>
      {
        error
        ? <Alert severity='error'>{error.message}</Alert>
        : null
      }
      {
        vacationTimes && vacationTimes.length
        ? <ListVacationTimes
          vacationTimes={vacationTimes}
          onCreate={() => setVacationDialogIsOpen(true)}
          onDelete={({id}) => onDelete(id)}
        />
        : <Button
          variant='secondaryGradient'
          size={props.isMobile ? 'medium' : 'large'}
          color='secondary'
          onClick={() => setVacationDialogIsOpen(true)}
        >
          Away Time
        </Button>
      }
      <VacationTimeDialog
        isOpen={vacationDialogIsOpen}
        onClose={() => setVacationDialogIsOpen(false)}
        onSubmit={onSubmit}
      />
    </Box>
  );
};

export default VacationTimeEditor;
